import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropertyCard from '../components/PropertyCard';
import FilterBar from '../components/FilterBar';
import { MapSearchView } from '../components/map-search';
import SearchBar from '../components/SearchBar';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LayoutGrid, Map, ArrowUpDown, Loader2 } from 'lucide-react';
import { useProperties } from '../hooks/useSanityData';
import { LatLngExpression } from 'leaflet';
import { FilterState, SortOption } from '../types';

const PropertiesPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial view mode from URL
  const viewFromUrl = searchParams.get('view') as 'grid' | 'map' | null;
  const [viewMode, setViewMode] = useState<'grid' | 'map'>(viewFromUrl || 'grid');

  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const { properties: allProperties, loading } = useProperties();
  const [filters, setFilters] = useState<FilterState>({
    propertyType: 'all',
    priceRange: 'any',
    priceMin: 0,
    priceMax: 2000000,
    bedrooms: 'any',
    bathrooms: 'any',
    sqftMin: '',
    sqftMax: '',
    yearBuiltMin: '',
    yearBuiltMax: '',
    lotSize: 'any',
    hasHOA: 'any',
    hoaFeesMax: '',
    parkingMin: '',
    keywords: '',
    daysOnMarket: 'any',
    openHousesOnly: false,
    virtualToursOnly: false
  });

  const searchQuery = searchParams.get('search') || '';

  // Sync view mode with URL
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (viewMode === 'grid') {
      newParams.delete('view');
    } else {
      newParams.set('view', viewMode);
    }
    // Only update if actually different to prevent infinite loop
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [viewMode, searchParams, setSearchParams]);

  // Filter properties based on search and filters
  const filteredProperties = useMemo(() => {
    let result = [...allProperties];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (property) =>
          property.city.toLowerCase().includes(query) ||
          property.state.toLowerCase().includes(query) ||
          property.address.toLowerCase().includes(query) ||
          property.zipCode.includes(query) ||
          property.title.en.toLowerCase().includes(query) ||
          property.title.es.toLowerCase().includes(query) ||
          property.description.en.toLowerCase().includes(query) ||
          property.description.es.toLowerCase().includes(query) ||
          property.features.some(feat => feat.en.toLowerCase().includes(query)) ||
          property.features.some(feat => feat.es.toLowerCase().includes(query))
      );
    }

    // Property type filter
    if (filters.propertyType !== 'all') {
      result = result.filter(
        (property) => property.propertyType.toLowerCase() === filters.propertyType
      );
    }

    // Price range filter - using slider values
    if (filters.priceMin > 0 || filters.priceMax < 2000000) {
      result = result.filter(
        (property) => property.price >= filters.priceMin && property.price <= filters.priceMax
      );
    }

    // Bedrooms filter
    if (filters.bedrooms !== 'any') {
      const minBeds = parseInt(filters.bedrooms);
      result = result.filter((property) => property.beds >= minBeds);
    }

    // Bathrooms filter
    if (filters.bathrooms !== 'any') {
      const minBaths = parseInt(filters.bathrooms);
      result = result.filter((property) => property.baths >= minBaths);
    }

    // Square footage filter
    if (filters.sqftMin) {
      const minSqft = parseInt(filters.sqftMin);
      result = result.filter((property) => property.sqft >= minSqft);
    }
    if (filters.sqftMax) {
      const maxSqft = parseInt(filters.sqftMax);
      result = result.filter((property) => property.sqft <= maxSqft);
    }

    // Year built filter
    if (filters.yearBuiltMin) {
      const minYear = parseInt(filters.yearBuiltMin);
      result = result.filter((property) => property.yearBuilt >= minYear);
    }
    if (filters.yearBuiltMax) {
      const maxYear = parseInt(filters.yearBuiltMax);
      result = result.filter((property) => property.yearBuilt <= maxYear);
    }

    // Lot size filter
    if (filters.lotSize !== 'any') {
      result = result.filter((property) => {
        const lotSize = property.lotSize.toLowerCase();
        if (filters.lotSize === 'small (< 5,000 sqft)') {
          return lotSize.includes('n/a') || parseInt(lotSize.replace(/,/g, '')) < 5000;
        } else if (filters.lotSize === 'medium (5,000-10,000 sqft)') {
          const size = parseInt(lotSize.replace(/,/g, ''));
          return size >= 5000 && size <= 10000;
        } else if (filters.lotSize === 'large (> 10,000 sqft)') {
          const size = parseInt(lotSize.replace(/,/g, ''));
          return size > 10000;
        }
        return true;
      });
    }

    // HOA filter
    if (filters.hasHOA !== 'any') {
      result = result.filter((property) => {
        const hasHOA = property.hoaFees !== undefined && property.hoaFees > 0;
        return filters.hasHOA === 'yes' ? hasHOA : !hasHOA;
      });
    }

    // HOA fees max filter
    if (filters.hoaFeesMax) {
      const maxHOA = parseInt(filters.hoaFeesMax);
      result = result.filter((property) => {
        if (!property.hoaFees) return true;
        return property.hoaFees <= maxHOA;
      });
    }

    // Parking filter
    if (filters.parkingMin) {
      const minParking = parseInt(filters.parkingMin);
      result = result.filter((property) => property.parking >= minParking);
    }

    // Keywords filter
    if (filters.keywords) {
      const keywords = filters.keywords.toLowerCase().split(',').map(k => k.trim());
      result = result.filter((property) => {
        const searchText = [
          property.title.en,
          property.title.es,
          property.description.en,
          property.description.es,
          ...property.features.map(feat => feat.en),
          ...property.features.map(feat => feat.es)
        ].join(' ').toLowerCase();
        return keywords.some(keyword => searchText.includes(keyword));
      });
    }

    // Days on market filter
    if (filters.daysOnMarket !== 'any') {
      result = result.filter((property) => {
        if (filters.daysOnMarket === 'new (< 7 days)') {
          return property.daysOnMarket < 7;
        } else if (filters.daysOnMarket === 'recent (< 14 days)') {
          return property.daysOnMarket < 14;
        } else if (filters.daysOnMarket === 'active (< 30 days)') {
          return property.daysOnMarket < 30;
        }
        return true;
      });
    }

    // Open houses only filter
    if (filters.openHousesOnly) {
      result = result.filter((property) => property.openHouseDate !== undefined);
    }

    // Virtual tours only filter
    if (filters.virtualToursOnly) {
      result = result.filter((property) => property.virtualTourUrl !== undefined);
    }

    // Sort properties
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => a.daysOnMarket - b.daysOnMarket);
        break;
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'beds':
        result.sort((a, b) => b.beds - a.beds);
        break;
      case 'sqft':
        result.sort((a, b) => b.sqft - a.sqft);
        break;
    }

    // Push sold properties to the end
    result.sort((a, b) => (a.sold ? 1 : 0) - (b.sold ? 1 : 0));

    return result;
  }, [searchQuery, filters, sortBy, allProperties]);

  const handleFilterChange = (filterName: keyof FilterState, value: string | boolean | number) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      propertyType: 'all',
      priceRange: 'any',
      priceMin: 0,
      priceMax: 2000000,
      bedrooms: 'any',
      bathrooms: 'any',
      sqftMin: '',
      sqftMax: '',
      yearBuiltMin: '',
      yearBuiltMax: '',
      lotSize: 'any',
      hasHOA: 'any',
      hoaFeesMax: '',
      parkingMin: '',
      keywords: '',
      daysOnMarket: 'any',
      openHousesOnly: false,
      virtualToursOnly: false
    });
  };

  // Calculate center for map based on filtered properties
  const mapCenter = useMemo((): LatLngExpression => {
    if (filteredProperties.length === 0) return [-25.2867, -57.5759]; // Default to Asunción, Paraguay
    const avgLat = filteredProperties.reduce((sum, p) => sum + p.latitude, 0) / filteredProperties.length;
    const avgLng = filteredProperties.reduce((sum, p) => sum + p.longitude, 0) / filteredProperties.length;
    return [avgLat, avgLng];
  }, [filteredProperties]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  // Render full-page map view
  if (viewMode === 'map') {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        {/* Minimal header with search and view toggle */}
        <div className="bg-white border-b border-gray-200 py-3 px-4 flex items-center justify-between">
          <div className="flex-1 max-w-xl">
            <SearchBar variant="compact" />
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 ml-4">
            <Button
              onClick={() => setViewMode('grid')}
              variant="outline"
              size="sm"
            >
              <LayoutGrid className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('filters.grid')}</span>
            </Button>
            <Button
              onClick={() => setViewMode('map')}
              variant="default"
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Map className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('filters.map')}</span>
            </Button>
          </div>
        </div>

        {/* Full-page Map Search View */}
        <MapSearchView
          properties={filteredProperties}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          loading={loading}
          sortBy={sortBy}
          onSortChange={setSortBy}
          mapCenter={mapCenter}
          onViewChange={setViewMode}
        />
      </div>
    );
  }

  // Render grid view (default)
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Search Bar Section */}
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SearchBar variant="compact" />
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Results Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {filteredProperties.length} {filteredProperties.length === 1 ? t('common.home') : t('common.homes')}
              </h2>
              {searchQuery && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('search.resultsFor')} "{searchQuery}"
                </p>
              )}
            </div>

            {/* Sort and View Controls */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Sort Dropdown */}
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-gray-500 hidden sm:block" />
                <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                  <SelectTrigger className="w-[140px] sm:w-[180px] text-sm">
                    <SelectValue placeholder={t('filters.sortBy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">{t('filters.sortNewest')}</SelectItem>
                    <SelectItem value="price-low">{t('filters.sortPriceLow')}</SelectItem>
                    <SelectItem value="price-high">{t('filters.sortPriceHigh')}</SelectItem>
                    <SelectItem value="beds">{t('filters.sortBeds')}</SelectItem>
                    <SelectItem value="sqft">{t('filters.sortSqft')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setViewMode('grid')}
                  variant="default"
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <LayoutGrid className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('filters.grid')}</span>
                </Button>
                <Button
                  onClick={() => setViewMode('map')}
                  variant="outline"
                  size="sm"
                >
                  <Map className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('filters.map')}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area - Grid View */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredProperties.length === 0 ? (
          <div className="text-center py-20">
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">{t('search.noPropertiesFound')}</h3>
            <p className="text-gray-600 mb-6">{t('search.tryAdjusting')}</p>
            <Button onClick={handleClearFilters} className="bg-blue-600 hover:bg-blue-700">
              {t('filters.clearAllFilters')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPage;
